import { type FC, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import { useHomePageContext } from '../HomePageContext.tsx';
import HpSharedVideoComponentTitle from './HpSharedVideoComponentTitle.tsx';
import HpSharedVideoComponentVideo from './HpSharedVideoComponentVideo.tsx';
import HpSharedVideoComponentCarousel from './HpSharedVideoComponentCarousel.tsx';
import { trackVideoModuleDisplay } from './videoComponentTrackingHelpers.ts';

import { type HpSharedVideoComponent_user$key } from './__generated__/HpSharedVideoComponent_user.graphql.ts';
import { type HpSharedVideoComponent_componentModule$key } from './__generated__/HpSharedVideoComponent_componentModule.graphql.ts';

import styles from './HpSharedVideoComponent.module.css';

const isSellerStorefrontCtaLink = (pathname: string): boolean => {
    const pathOnly = pathname.split('?')[0].split('#')[0];
    const normalized = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
    return /^\/(?:[a-z]{2}\/)?dealers\/[^/]+\/?$/i.test(normalized);
};

const getSellerStorefrontShopUriRef = (ctaLink?: string | null): string | null => {
    if (!ctaLink || !isSellerStorefrontCtaLink(ctaLink)) {
        return null;
    }
    const suffix = ctaLink.endsWith('/') ? 'shop/' : '/shop/';
    return `${ctaLink}${suffix}?sort=recommended`;
};

type Props = {
    user?: HpSharedVideoComponent_user$key | null;
    fetchFavorites: boolean;
    componentModule: HpSharedVideoComponent_componentModule$key;
};

const userFragment = graphql`
    fragment HpSharedVideoComponent_user on User {
        ...HpSharedVideoComponentCarousel_user
    }
`;

const componentModuleFragment = graphql`
    fragment HpSharedVideoComponent_componentModule on VideoModule {
        userType
        videoId
        ctaLink
        ...HpSharedVideoComponentTitle_componentModule
        ...HpSharedVideoComponentVideo_componentModule
    }
`;

const HpSharedVideoComponent: FC<Props> = ({
    user: userRef,
    componentModule: componentModuleRef,
    fetchFavorites,
}) => {
    const { isMobile, userType } = useHomePageContext();
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);
    const user = useFragment(userFragment, userRef ?? null);
    const videoRef = useRef(null);

    const { userType: moduleUserType, videoId } = componentModule || {};
    const upperUserType = userType?.toUpperCase();
    const shouldShowForUserType =
        !moduleUserType || moduleUserType === 'ALL' || moduleUserType === upperUserType;

    if (!shouldShowForUserType || !videoId) {
        return null;
    }

    const storefrontShopUriRef = getSellerStorefrontShopUriRef(componentModule.ctaLink);

    return (
        <>
            <VisibilityTracker
                elementRef={videoRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackVideoModuleDisplay();
                    }
                }}
            />
            <div
                ref={videoRef}
                className={classnames(dibsCss.wScreen, dibsCss.bgBuyertertiary, styles.container, {
                    [dibsCss.pb0]: !!storefrontShopUriRef,
                    [dibsCss.pLarger]: !isMobile,
                    [dibsCss.pSmall]: isMobile,
                    [dibsCss.mtLarger]: !isMobile,
                    [dibsCss.mtSmall]: isMobile,
                })}
            >
                <div className={classnames(dibsCss.maxW1440px, dibsCss.my0, dibsCss.mxAuto)}>
                    {isMobile ? (
                        <div className={dibsCss.mtSmall}>
                            <HpSharedVideoComponentTitle componentModule={componentModule} />
                            <div className={dibsCss.mtMedium}>
                                <div className="primary-viewport-width">
                                    <HpSharedVideoComponentVideo
                                        componentModule={componentModule}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={dibsCss.flex}>
                            <HpSharedVideoComponentVideo componentModule={componentModule} />
                            <div className={classnames(dibsCss.mlXlarge, dibsCss.contentCenter)}>
                                <HpSharedVideoComponentTitle componentModule={componentModule} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {storefrontShopUriRef ? (
                <div
                    className={classnames(
                        dibsCss.bgBuyertertiary,
                        dibsCss.wScreen,
                        styles.container,
                        {
                            [dibsCss.pxLarge]: !isMobile,
                            [dibsCss.pyLarger]: !isMobile,
                            [dibsCss.pxSmall]: isMobile,
                            [dibsCss.pyMedium]: isMobile,
                        }
                    )}
                >
                    <div className={classnames(dibsCss.maxW1440px, dibsCss.my0, dibsCss.mxAuto)}>
                        <div className={isMobile ? 'primary-viewport-width' : undefined}>
                            <HpSharedVideoComponentCarousel
                                uriRef={storefrontShopUriRef}
                                user={user}
                                fetchFavorites={fetchFavorites}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default HpSharedVideoComponent;
